import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { TemplateStudio } from "@/components/scheduled-briefings/template-studio/TemplateStudio";
import { TemplateLayoutLoadStatus } from "@/components/scheduled-briefings/templates/TemplateLayoutLoadStatus";
import TemplatePreview from "@/components/scheduled-briefings/templates/templates-preview/TemplatePreview";
import useFeature from "@/lib/feature-management/useFeature";
import { useTemplateLayout } from "@/lib/template-studio/repository/useTemplateLayout";
import { canOpenTemplateInTemplateStudio } from "@/lib/template-studio/templateStudioEntry";
import type { TemplateLayout } from "@/types/template-layout/templateLayout.types";

const routeApi = getRouteApi(
  "/_authenticated/scheduled-briefings/templates/$templateId",
);

const LoadedTemplate = ({
  template,
  canCreateTemplates,
  showDetails,
}: {
  template: TemplateLayout;
  canCreateTemplates: boolean;
  showDetails: boolean;
}) => {
  // One-way latch: the flag may resolve after the template loads, so preview can
  // become Studio, but never the reverse (that would unmount Studio and lose the draft).
  const canOpen = canOpenTemplateInTemplateStudio(template, canCreateTemplates);
  const [opensInTemplateStudio, setOpensInTemplateStudio] = useState(canOpen);
  if (canOpen && !opensInTemplateStudio) {
    setOpensInTemplateStudio(true);
  }

  return opensInTemplateStudio && !showDetails ? (
    <TemplateStudio mode="edit" template={template} />
  ) : (
    <TemplatePreview template={template} />
  );
};

/**
 * Owners of active templates no briefing uses edit in Template Studio; everyone
 * else, and `?view=details`, gets the locked preview.
 */
const TemplatePreviewRoute = () => {
  const { templateId } = routeApi.useParams();
  const { view } = routeApi.useSearch();
  const canCreateTemplates = useFeature("can-create-templates", false);
  const { template, error, isRetryable, retry } = useTemplateLayout(templateId);

  if (template == null) {
    return (
      <TemplateLayoutLoadStatus
        error={error}
        isRetryable={isRetryable}
        retry={retry}
      />
    );
  }

  return (
    <LoadedTemplate
      key={template.id}
      template={template}
      canCreateTemplates={canCreateTemplates}
      showDetails={view === "details"}
    />
  );
};

export default TemplatePreviewRoute;
